import Dexie, { type Table } from 'dexie';

export interface SealedValue { version: 1; iv: string; ciphertext: string }
const encoder = new TextEncoder();
const decoder = new TextDecoder();
let keysPromise: Promise<{ encryption: CryptoKey; index: CryptoKey }> | undefined;

export function encodeBase64(bytes: Uint8Array): string {
  let text = '';
  for (const byte of bytes) text += String.fromCharCode(byte);
  return btoa(text);
}
export function decodeBase64(text: string): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(atob(text), character => character.charCodeAt(0));
}

// Dates must survive a round trip; JSON's default Date.toJSON loses the type.
export function serialize(value: unknown): string {
  return JSON.stringify(value, function (key, item) {
    return this[key] instanceof Date ? { $fusicsDate: this[key].toISOString() } : item;
  });
}
export function deserialize<T>(text: string): T {
  return JSON.parse(text, (_key, item) => item && typeof item === 'object'
    && Object.keys(item).length === 1 && typeof item.$fusicsDate === 'string'
    ? new Date(item.$fusicsDate) : item) as T;
}

async function keys() {
  keysPromise ??= (async () => {
    if (!globalThis.crypto?.subtle) throw new Error('Sichere lokale Speicherung ist in diesem Browser nicht verfügbar.');
    const database = new Dexie('fusics-vault-keys-v1');
    database.version(1).stores({ keys: 'name' });
    const table = database.table('keys') as Table<{ name: string; key: CryptoKey }, string>;
    // Generate candidates before the transaction, then choose atomically across tabs.
    const encryption = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    const index = await crypto.subtle.generateKey({ name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    try {
      return await database.transaction('rw', table, async () => {
        const storedEncryption = await table.get('encryption');
        const storedIndex = await table.get('index');
        if (Boolean(storedEncryption) !== Boolean(storedIndex)) throw new Error('Der lokale Tresorschlüssel ist unvollständig.');
        if (storedEncryption && storedIndex) return { encryption: storedEncryption.key, index: storedIndex.key };
        await table.bulkAdd([{ name: 'encryption', key: encryption }, { name: 'index', key: index }]);
        return { encryption, index };
      });
    } finally { database.close(); }
  })();
  return keysPromise;
}

export async function seal(value: unknown, context: string, key?: CryptoKey): Promise<SealedValue> {
  const encryption = key ?? (await keys()).encryption;
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: encoder.encode(context) }, encryption, encoder.encode(serialize(value)));
  return { version: 1, iv: encodeBase64(iv), ciphertext: encodeBase64(new Uint8Array(ciphertext)) };
}

export async function unseal<T>(box: SealedValue, context: string, key?: CryptoKey): Promise<T> {
  if (box?.version !== 1 || typeof box.iv !== 'string' || typeof box.ciphertext !== 'string') throw new Error('Ungültiges Tresorformat.');
  const encryption = key ?? (await keys()).encryption;
  const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: decodeBase64(box.iv), additionalData: encoder.encode(context) }, encryption, decodeBase64(box.ciphertext));
  return deserialize<T>(decoder.decode(plaintext));
}

export async function opaqueKey(context: string, value: unknown): Promise<string> {
  const signature = await crypto.subtle.sign('HMAC', (await keys()).index, encoder.encode(`${context}\u0000${serialize(value)}`));
  return encodeBase64(new Uint8Array(signature));
}

export async function backupKey(phrase: string, salt: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey('raw', encoder.encode(phrase.normalize('NFKC')), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations: 600_000 }, material, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}
